import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { CaptchaResponse } from "@/types/CaptchaTypes";
import { getCaptcha } from "@/api/captchaApi";

export interface CaptchaSolution {
    answer: string;
    payload: string;
}

interface ParizCaptchaProps {
    /** Fired when a complete (6-char) answer is ready — auto-submitted. */
    onSolved: (solution: CaptchaSolution) => void;
    /**
     * Fired on every keystroke with the current answer + payload, or `null`
     * when the input becomes empty. Use this if the parent wants the
     * always-current values (e.g. to send them in the login request).
     */
    onChange?: (solution: CaptchaSolution | null) => void;
    /** Called when the parent wants the state cleared. */
    onFailed?: (message: string) => void;
    /** External error → clears input and fetches a fresh image. */
    error?: string;
}

const CAPTCHA_LENGTH = 6;

const ParizCaptcha: React.FC<ParizCaptchaProps> = ({ onSolved, onChange, onFailed, error }) => {
    const [data, setData] = useState<CaptchaResponse | null>(null);
    const [answer, setAnswer] = useState("");
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState("");

    // Guards against submitting the same (payload, answer) pair twice.
    const lastSubmittedRef = useRef<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLocalError("");
        setAnswer("");
        setData(null);
        lastSubmittedRef.current = null;
        try {
            setData(await getCaptcha());
        } catch {
            setLocalError("خطا در دریافت کپچا");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    // Parent signals failure → show error, fetch a fresh image.
    useEffect(() => {
        if (error) {
            setLocalError(error);
            void load();
        }
    }, [error, load]);

    const submit = useCallback(
        (value: string) => {
            const trimmed = value.trim();
            if (trimmed.length !== CAPTCHA_LENGTH) return;
            if (!data) return;

            const key = `${data.payload}:${trimmed}`;
            if (lastSubmittedRef.current === key) return;

            lastSubmittedRef.current = key;
            setLocalError("");
            onSolved({ answer: trimmed, payload: data.payload });
        },
        [data, onSolved],
    );

    // Auto-submit when 6 chars are present.
    useEffect(() => {
        if (loading || !data) return;
        if (answer.trim().length !== CAPTCHA_LENGTH) return;
        submit(answer);
    }, [answer, data, loading, submit]);

    // Notify parent on every change so it always has the up-to-date solution.
    useEffect(() => {
        if (!data) { onChange?.(null); return; }
        const trimmed = answer.trim();
        if (trimmed.length === 0) { onChange?.(null); return; }
        onChange?.({ answer: trimmed, payload: data.payload });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answer, data]);

    const handleChange = (value: string) => {
        // Invalidate any previous successful submission — a different answer
        // will need a different submission.
        lastSubmittedRef.current = null;
        setAnswer(value);
    };

    const disabled = loading;

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                    sx={{
                        minWidth: 130, height: 48, display: "flex",
                        alignItems: "center", justifyContent: "center",
                        border: "1px solid #ccc", borderRadius: 1, overflow: "hidden", bgcolor: "#fafafa", px: 1,
                    }}
                >
                    {loading ? <CircularProgress size={20} /> :
                        data?.base64 ? <img src={data.base64} alt="captcha" style={{ height: 40, objectFit: "contain", userSelect: "none" }} /> : null}
                </Box>

                <TextField
                    size="small"
                    value={answer}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="کد تصویر"
                    inputProps={{ maxLength: CAPTCHA_LENGTH, dir: "ltr" }}
                    sx={{ flex: 1 }}
                    disabled={disabled}
                />

                <Tooltip title="تصویر جدید">
                    <span>
                        <IconButton onClick={() => void load()} disabled={disabled}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {localError && <Box sx={{ color: "#ff6b6b", fontSize: 12 }}>{localError}</Box>}
        </Box>
    );
};

export default ParizCaptcha;